"use client";

import type { ReactNode } from "react";
import clsx from "clsx";
import { Search } from "lucide-react";
import { MCAP_OPTIONS } from "./logic";
import { inputCls } from "./filter-builder";

export function SearchBox({
  value,
  onChange,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  placeholder: string;
}) {
  return (
    <div className="relative w-full sm:w-64">
      <Search size={13} className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-muted" />
      <input
        type="search"
        aria-label="ค้นหาหุ้น"
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={clsx(inputCls, "w-full pl-8")}
      />
    </div>
  );
}

export function SelectBox({
  value,
  onChange,
  options,
  label,
}: {
  value: string;
  onChange: (v: string) => void;
  options: readonly { value: string; label: string }[];
  label: string;
}) {
  return (
    <select aria-label={label} value={value} onChange={(e) => onChange(e.target.value)} className={inputCls}>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

export function McapSelect({ value, onChange }: { value: string; onChange: (v: string) => void }) {
  return <SelectBox label="Market cap" value={value} onChange={onChange} options={MCAP_OPTIONS} />;
}

/** Toggleable count badge (stockweb "Breakout: N" / "Stage 2: N" buttons). */
export function CountBadge({
  active,
  onClick,
  tone,
  children,
}: {
  active: boolean;
  onClick: () => void;
  tone: "up" | "down" | "accent" | "sky" | "amber" | "muted";
  children: ReactNode;
}) {
  const toneCls: Record<typeof tone, string> = {
    up: "border-up/30 text-up",
    down: "border-down/30 text-down",
    accent: "border-accent/40 text-accent",
    sky: "border-sky-500/30 text-sky-400",
    amber: "border-amber-500/30 text-amber-400",
    muted: "border-border text-muted",
  };
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={clsx(
        "rounded-full border px-3 py-1 text-xs font-semibold tabular-nums transition-colors",
        toneCls[tone],
        active ? "bg-white/10 ring-1 ring-current" : "bg-surface-2 hover:bg-white/[0.06]",
      )}
    >
      {children}
    </button>
  );
}

export const QUADRANT_CLS: Record<string, string> = {
  Leading: "text-up",
  Improving: "text-sky-400",
  Weakening: "text-amber-400",
  Lagging: "text-down",
};

/** stockweb RS colouring for the scanner / stage tables (≥90/80 or ≥80/70 tiers). */
export function RsValue({ v, hi, mid }: { v: number | null; hi: number; mid: number }) {
  if (v == null) return <span className="text-muted">—</span>;
  return (
    <span className={clsx("tabular-nums", v >= hi ? "font-bold text-up" : v >= mid ? "text-sky-400" : "text-foreground/80")}>
      {v}
    </span>
  );
}

export function TickerCell({ ticker }: { ticker: string }) {
  // TODO(chart): stockweb shows a hover MiniChart popover (price + EMA9/20/50 +
  // MACD, ~120 daily bars from /api/chart). Add it once a chart API exists.
  return <span className="font-mono text-sm font-semibold">{ticker}</span>;
}
